import {useMemo, useState} from 'react';
import {useQueryClient} from 'react-query';
import {ColumnDef} from '@tanstack/react-table';
import {useIntl} from 'react-intl';
import axios from 'axios';
import {
  Alert,
  Button as BootstrapButton,
  Card,
  Col,
  Form,
  ListGroup,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap';
import TableMetronic from '../../../../../app/common/components/table/TableMetronic';
import Badge from '../../../../../app/common/components/Badge';
import Level3Title from '../../../../../app/common/components/title/Level3Title';
import ActionIcon from '../../../../../Components/ActionIcon';
import Button, {faIconAdd} from '../../../../../Components/Button';
import Loading from '../../../../../app/common/components/Loading';
import {LocalisationSite} from './mocks';
import {AddressSuggestion} from '../../../Api/telephonie';
import {
  useAjouterLocalisationUrgence,
  useGetLocalisationsUrgence,
  useGetTrunkNumeros,
  useMettreAJourNumero,
  useModifierLocalisationUrgence,
  useSearchAddresses,
  useSupprimerLocalisationUrgence,
  useSwissBuildingReference,
} from '../../../Hooks/TelephonieManager';
import {useAutoDismissMessage} from '../../../Hooks/useAutoDismissMessage';

interface FormulaireSite {
  nom: string;
  client: string;
  rue: string;
  numero: string;
  cp: string;
  ville: string;
  pays: string;
  defaut: boolean;
  /** Renseignés à la sélection d'une suggestion EGLS ; effacés si l'adresse est modifiée à la main ensuite. */
  municipalityId?: number;
  egid?: string;
  inseeCode?: string;
  latitude?: number;
  longitude?: number;
}

const SITE_VIDE: FormulaireSite = {
  nom: '',
  client: '',
  rue: '',
  numero: '',
  cp: '',
  ville: '',
  pays: 'France',
  defaut: false,
};

const siteEnFormulaire = (site: LocalisationSite): FormulaireSite => ({
  nom: site.nom,
  client: site.client,
  rue: site.rue,
  numero: site.numero,
  cp: site.cp,
  ville: site.ville,
  pays: site.pays,
  defaut: site.defaut,
  municipalityId: site.municipalityId,
  egid: site.egid,
  inseeCode: site.inseeCode,
  latitude: site.latitude,
  longitude: site.longitude,
});

/** Efface les données géo capturées : à appeler dès que l'utilisateur modifie l'adresse à la main. */
const sansDonneesGeo = (formulaire: FormulaireSite): FormulaireSite => ({
  ...formulaire,
  municipalityId: undefined,
  egid: undefined,
  inseeCode: undefined,
  latitude: undefined,
  longitude: undefined,
});

/**
 * L'API EGLS renvoie un `label` avec le CP/ville entouré d'une balise
 * `<b>` (ex. "Bahnhofstrasse 1 <b>8001 Zürich</b>"). On découpe la chaîne
 * nous-mêmes et on rend la partie médiane dans un vrai `<strong>` JSX —
 * chaque segment reste un noeud texte échappé par React, donc aucun HTML
 * arbitraire de cette API tierce n'est jamais interprété (pas de
 * `dangerouslySetInnerHTML`).
 */
const labelSuggestionAvecGras = (label: string): JSX.Element => {
  const correspondance = label.match(/^(.*?)<b>(.*?)<\/b>(.*)$/);

  if (!correspondance) {
    return <>{label}</>;
  }

  const [, avant, gras, apres] = correspondance;

  return (
    <>
      {avant}
      <strong>{gras}</strong>
      {apres}
    </>
  );
};

/**
 * Onglet « Localisations d'urgence » d'une prestation Trunk SIP.
 *
 * Les sites (`sites`) viennent de l'API (api-telephonie, ressource Location) ;
 * ajout/modification/suppression appellent les endpoints correspondants et
 * invalident le cache React Query pour refléter le nouvel état. L'affectation
 * de numéros à un site persiste elle aussi côté API (`PUT .../number/{id}`
 * avec `locationId`), un numéro à la fois pour chaque numéro (dé)sélectionné.
 */
interface LocalisationsUrgenceProps {
  serviceId: number;
  isActive?: boolean;
}

const LocalisationsUrgence = ({
  serviceId,
  isActive = true,
}: LocalisationsUrgenceProps): JSX.Element | null => {
  const intl = useIntl();
  const {data, isLoading, isError} = useGetLocalisationsUrgence(serviceId, isActive);
  const {data: numerosData} = useGetTrunkNumeros(serviceId, isActive);
  /** Un numéro réservé (stock non attribué) ne peut pas être rattaché à un site. */
  const numerosAssignables = useMemo(
    () => (numerosData?.numeros ?? []).filter((numero) => !numero.reserve),
    [numerosData]
  );
  const ajouterMutation = useAjouterLocalisationUrgence(serviceId);
  const modifierMutation = useModifierLocalisationUrgence(serviceId);
  const supprimerMutation = useSupprimerLocalisationUrgence(serviceId);
  // La liste des numéros n'est rafraîchie qu'une fois à la fin de l'affectation
  // (voir `confirmerAffectation`), pas après chaque numéro traité.
  const mettreAJourNumeroApi = useMettreAJourNumero(serviceId, {invalidateOnSuccess: false});
  const queryClient = useQueryClient();

  const sites = useMemo(() => data?.sites ?? [], [data]);

  const [showForm, setShowForm] = useState<boolean>(false);
  const [siteEnEdition, setSiteEnEdition] = useState<LocalisationSite | null>(null);
  const [formulaire, setFormulaire] = useState<FormulaireSite>(SITE_VIDE);
  const [siteASupprimer, setSiteASupprimer] = useState<LocalisationSite | null>(null);
  const [siteAAffecter, setSiteAAffecter] = useState<LocalisationSite | null>(null);
  const [numerosSelectionnes, setNumerosSelectionnes] = useState<string[]>([]);
  const [numerosInitiaux, setNumerosInitiaux] = useState<string[]>([]);
  const [message, setMessage] = useAutoDismissMessage();
  const [rechercheAdresseActive, setRechercheAdresseActive] = useState<boolean>(false);
  const [progressionAffectation, setProgressionAffectation] = useState<{
    fait: number;
    total: number;
  } | null>(null);

  const paysGereParEgls = formulaire.pays === 'France' || formulaire.pays === 'Suisse';
  const codePaysEgls: 'FR' | 'CH' = formulaire.pays === 'Suisse' ? 'CH' : 'FR';
  // Contrainte EGLS confirmée empiriquement : pour la France, `zipcode` est
  // requis (la recherche échoue sans lui) et `city` est ignoré côté client
  // (voir EglsServiceClient::searchAddresses) — donc pas de recherche tant
  // que le code postal n'est pas renseigné. La Suisse n'a pas cette contrainte.
  const codePostalRequisPourRecherche = codePaysEgls === 'FR' && formulaire.cp.trim() === '';
  const rechercheEglsActivee =
    rechercheAdresseActive && paysGereParEgls && !codePostalRequisPourRecherche;
  const {data: suggestions, isFetching: suggestionsEnCours} = useSearchAddresses(
    formulaire.rue,
    codePaysEgls,
    rechercheEglsActivee,
    formulaire.cp,
    formulaire.ville
  );
  const resolutionBatiment = useSwissBuildingReference();

  const selectionnerSuggestion = (suggestion: AddressSuggestion): void => {
    setRechercheAdresseActive(false);
    setFormulaire((precedent) => ({
      ...precedent,
      rue: suggestion.street ?? precedent.rue,
      numero: suggestion.houseNumber ?? precedent.numero,
      cp: suggestion.zip ?? precedent.cp,
      ville: suggestion.city ?? precedent.ville,
      inseeCode: suggestion.inseeCode,
      egid: undefined,
      municipalityId: undefined,
      latitude: suggestion.latitude,
      longitude: suggestion.longitude,
    }));

    if (codePaysEgls === 'CH' && suggestion.featureId) {
      resolutionBatiment.mutateAsync(suggestion.featureId).then((reference) => {
        if (!reference) {
          return;
        }
        setFormulaire((precedent) => ({
          ...precedent,
          municipalityId: reference.municipalityId ?? precedent.municipalityId,
          egid: reference.egid ?? precedent.egid,
          latitude: reference.latitude ?? precedent.latitude,
          longitude: reference.longitude ?? precedent.longitude,
        }));
      });
    }
  };

  const ouvrirAffectation = (site: LocalisationSite): void => {
    const numerosDuSite = numerosAssignables
      .filter((numero) => numero.locationId === site.id)
      .map((numero) => numero.numero);
    setNumerosSelectionnes(numerosDuSite);
    setNumerosInitiaux(numerosDuSite);
    setSiteAAffecter(site);
    setMessage(null);
  };

  /**
   * Un numéro nouvellement coché est rattaché au site en cours d'édition ; un
   * numéro décoché est désaffecté (`locationId: 0` — confirmé côté
   * api-telephonie : c'est la valeur qui signifie « aucun site » pour un
   * numéro, IdSite à 0), sans jamais retomber sur le site par défaut.
   */
  const confirmerAffectation = async (): Promise<void> => {
    if (!siteAAffecter) {
      return;
    }
    const numerosParNumero = new Map(numerosAssignables.map((n) => [n.numero, n]));
    const numerosNouvellementSelectionnes = numerosSelectionnes.filter(
      (numero) => !numerosInitiaux.includes(numero)
    );
    const numerosDeselectionnes = numerosInitiaux.filter(
      (numero) => !numerosSelectionnes.includes(numero)
    );

    const changements: {numeroId: number; locationId: number}[] = [];
    numerosNouvellementSelectionnes.forEach((numero) => {
      const numeroData = numerosParNumero.get(numero);
      if (numeroData) {
        changements.push({numeroId: numeroData.numeroId, locationId: siteAAffecter.id});
      }
    });
    numerosDeselectionnes.forEach((numero) => {
      const numeroData = numerosParNumero.get(numero);
      if (numeroData) {
        changements.push({numeroId: numeroData.numeroId, locationId: 0});
      }
    });

    // Envoyés un par un plutôt qu'en parallèle (`Promise.all`) : api-telephonie
    // rejette certaines requêtes (« The remote service could not be reached »)
    // quand une affectation en masse (ex. tout le trunk vers un site) lui
    // envoie trop d'appels simultanés. La mutation retente déjà les échecs
    // transitoires (voir `useMettreAJourNumero`).
    setProgressionAffectation({fait: 0, total: changements.length});
    try {
      for (let index = 0; index < changements.length; index += 1) {
        const {numeroId, locationId} = changements[index];
        await mettreAJourNumeroApi.mutateAsync({numeroId, payload: {locationId}});
        setProgressionAffectation({fait: index + 1, total: changements.length});
      }
      setSiteAAffecter(null);
      setMessage(intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.SUCCESS_MESSAGE'}));
    } catch {
      setMessage(intl.formatMessage({id: 'COMMON.INTERNAL_ERROR'}));
    } finally {
      // Un seul rafraîchissement de la liste, même en cas d'échec partiel
      // (certains numéros peuvent avoir été mis à jour avant l'erreur).
      await queryClient.invalidateQueries(['trunk-sip-numeros', serviceId]);
      setProgressionAffectation(null);
    }
  };

  const ouvrirAjout = (): void => {
    setSiteEnEdition(null);
    setFormulaire(SITE_VIDE);
    setShowForm(true);
    setRechercheAdresseActive(false);
    setMessage(null);
  };

  const ouvrirModifier = (site: LocalisationSite): void => {
    setSiteEnEdition(site);
    setFormulaire(siteEnFormulaire(site));
    setShowForm(true);
    setRechercheAdresseActive(false);
    setMessage(null);
  };

  const fermerFormulaire = (): void => {
    setShowForm(false);
    setSiteEnEdition(null);
  };

  /**
   * La plateforme voix legacy rejette (422) une adresse sans egid/inseeCode ou
   * sans coordonnées GPS (codes -17 à -20) ; ce cas précis (adresse saisie à la
   * main plutôt que sélectionnée dans les suggestions EGLS) a un message clair
   * plutôt que le `detail` technique anglais de la plateforme, relayé tel quel
   * pour les autres rejets.
   */
  const messageDeRejet = (err: unknown): string => {
    if (axios.isAxiosError(err) && typeof err.response?.data?.detail === 'string') {
      const detail = err.response.data.detail;
      if (/egid|inseeCode/i.test(detail)) {
        return intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.ERROR_ADRESSE_NON_GEOLOCALISEE'});
      }
      return detail;
    }
    return intl.formatMessage({id: 'COMMON.INTERNAL_ERROR'});
  };

  const validerFormulaire = (): void => {
    const promesse = siteEnEdition
      ? modifierMutation.mutateAsync({id: siteEnEdition.id, site: formulaire})
      : ajouterMutation.mutateAsync(formulaire);

    promesse
      .then(() => {
        fermerFormulaire();
        setMessage(intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.SUCCESS_MESSAGE'}));
      })
      .catch((err) => setMessage(messageDeRejet(err)));
  };

  const formulaireValide =
    formulaire.nom.trim() !== '' &&
    formulaire.client.trim() !== '' &&
    formulaire.rue.trim() !== '' &&
    formulaire.numero.trim() !== '' &&
    formulaire.cp.trim() !== '' &&
    formulaire.ville.trim() !== '' &&
    formulaire.pays.trim() !== '';

  const confirmerSuppression = (): void => {
    if (!siteASupprimer) {
      return;
    }
    supprimerMutation
      .mutateAsync(siteASupprimer.id)
      .then(() => setMessage(intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.SUCCESS_MESSAGE'})))
      .catch(() => setMessage(intl.formatMessage({id: 'COMMON.INTERNAL_ERROR'})))
      .finally(() => setSiteASupprimer(null));
  };

  const columns = useMemo<ColumnDef<LocalisationSite>[]>(
    () => [
      {
        header: intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.TABLE.COLUMN.ID'}),
        accessorKey: 'id',
        size: 70,
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.TABLE.COLUMN.DEFAULT'}),
        accessorKey: 'defaut',
        size: 110,
        cell: (info) =>
          info.row.original.defaut ? (
            <Badge
              text={intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.TABLE.COLUMN.DEFAULT'})}
              color="success"
              light
              size="small"
            />
          ) : (
            ''
          ),
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.TABLE.COLUMN.SITE_NAME'}),
        accessorKey: 'nom',
        size: 150,
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.TABLE.COLUMN.CLIENT'}),
        accessorKey: 'client',
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.TABLE.COLUMN.POSTAL_CODE'}),
        accessorKey: 'cp',
        size: 100,
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.TABLE.COLUMN.CITY'}),
        accessorKey: 'ville',
        size: 130,
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.TABLE.COLUMN.STREET'}),
        accessorKey: 'rue',
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.TABLE.COLUMN.STREET_NUMBER'}),
        accessorKey: 'numero',
        size: 80,
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.TABLE.COLUMN.COUNTRY'}),
        accessorKey: 'pays',
        size: 110,
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.TABLE.COLUMN.NUMBERS_USED'}),
        id: 'nombreNumeros',
        size: 220,
        enableSorting: false,
        cell: (info) => {
          const site = info.row.original;
          const nombre = numerosAssignables.filter(
            (numero) => numero.locationId === site.id
          ).length;
          return <Badge text={String(nombre)} color={nombre > 0 ? 'primary' : 'secondary'} light />;
        },
      },
      {
        header: intl.formatMessage({id: 'SERVICE.DOMAINE.TABLE.COLUMN.ACTIONS'}),
        id: 'actions',
        size: 150,
        enableSorting: false,
        cell: (info) => {
          const site = info.row.original;
          const siteUtilise =
            numerosAssignables.filter((numero) => numero.locationId === site.id).length > 0;
          return (
            <div>
              <ActionIcon
                label={intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.MODIFIER'})}
                faIcon="fs-3 bi bi-pencil"
                onClick={() => ouvrirModifier(site)}
              />
              <ActionIcon
                label={intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.ASSIGN.ACTION_LABEL'})}
                faIcon="fs-3 bi bi-telephone-plus"
                onClick={() => ouvrirAffectation(site)}
              />
              <ActionIcon
                label={intl.formatMessage(
                  siteUtilise
                    ? {id: 'TELEPHONIE.LOCALISATIONS.SUPPRIMER.DISABLED'}
                    : {id: 'TELEPHONIE.LOCALISATIONS.SUPPRIMER'}
                )}
                faIcon="fs-3 bi bi-trash text-danger"
                onClick={() => setSiteASupprimer(site)}
                disabled={siteUtilise}
              />
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [numerosAssignables, intl]
  );

  if (isError) {
    return <p>{intl.formatMessage({id: 'COMMON.INTERNAL_ERROR'})}</p>;
  }

  if (isLoading || !data) {
    return <Loading className="col-md-6" />;
  }

  return (
    <div className="mt-4">
      {message && <div className="fw-bold text-danger mb-3">{message}</div>}
      <Alert variant="info" className="d-flex align-items-center">
        <i className="bi bi-info-circle me-3 fs-3" />
        <span className="fs-6">
          {intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.ALERT_INFO'})}
        </span>
      </Alert>

      <TableMetronic<LocalisationSite>
        title={intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.TABLE_TITLE'})}
        data={sites}
        columns={columns}
        searchBar={false}
        stackedHeader
        toolbarEnd={
          !showForm ? (
            <Button
              label={intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.ADD_SITE'})}
              faIcon={faIconAdd}
              onClick={ouvrirAjout}
            />
          ) : undefined
        }
        elementName="site"
        elementNamePlural="sites"
      />

      {showForm && (
        <Card className="mt-5">
          <Card.Header>
            <Card.Title className="m-0">
              <Level3Title
                title={
                  siteEnEdition
                    ? intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.FORM.TITLE_EDIT'})
                    : intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.FORM.TITLE_ADD'})
                }
              />
            </Card.Title>
          </Card.Header>
          <Card.Body>
            <Row className="g-5 mb-5">
              <Col md={6}>
                <Form.Label className="fs-6">
                  {intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.FORM.LABEL_SITE_NAME'})}
                </Form.Label>
                <Form.Control
                  required
                  placeholder={intl.formatMessage({
                    id: 'TELEPHONIE.LOCALISATIONS.FORM.PLACEHOLDER_SITE_NAME',
                  })}
                  value={formulaire.nom}
                  onChange={(e) => setFormulaire({...formulaire, nom: e.target.value})}
                />
              </Col>
              <Col md={6}>
                <Form.Label className="fs-6">
                  {intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.FORM.LABEL_CLIENT_NAME'})}
                </Form.Label>
                <Form.Control
                  required
                  placeholder={intl.formatMessage({
                    id: 'TELEPHONIE.LOCALISATIONS.FORM.PLACEHOLDER_CLIENT_NAME',
                  })}
                  value={formulaire.client}
                  onChange={(e) => setFormulaire({...formulaire, client: e.target.value})}
                />
              </Col>
            </Row>
            <Row className="g-5 mb-5">
              <Col md={5} className="position-relative">
                <Form.Label className="fs-6">
                  {intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.FORM.LABEL_STREET'})}
                </Form.Label>
                <Form.Control
                  required
                  autoComplete="off"
                  value={formulaire.rue}
                  onChange={(e) =>
                    setFormulaire(sansDonneesGeo({...formulaire, rue: e.target.value}))
                  }
                  onFocus={() => setRechercheAdresseActive(true)}
                  onBlur={() => setTimeout(() => setRechercheAdresseActive(false), 200)}
                />
                {rechercheAdresseActive && paysGereParEgls && formulaire.rue.trim().length > 2 && (
                  <ListGroup
                    className="position-absolute w-100 mt-1 shadow-sm"
                    style={{zIndex: 1000, maxHeight: 260, overflowY: 'auto'}}
                  >
                    {codePostalRequisPourRecherche ? (
                      <ListGroup.Item className="text-muted">
                        {intl.formatMessage({
                          id: 'TELEPHONIE.LOCALISATIONS.FORM.ADDRESS_SEARCH.POSTAL_CODE_REQUIRED',
                        })}
                      </ListGroup.Item>
                    ) : suggestionsEnCours ? (
                      <ListGroup.Item className="d-flex align-items-center justify-content-center py-3">
                        <Spinner animation="border" size="sm" className="me-2" />
                        {intl.formatMessage({
                          id: 'TELEPHONIE.LOCALISATIONS.FORM.ADDRESS_SEARCH.SEARCHING',
                        })}
                      </ListGroup.Item>
                    ) : (suggestions ?? []).length > 0 ? (
                      (suggestions ?? []).map((suggestion) => (
                        <ListGroup.Item
                          key={suggestion.id}
                          action
                          className="cursor-pointer"
                          onClick={() => selectionnerSuggestion(suggestion)}
                        >
                          {labelSuggestionAvecGras(suggestion.label)}
                        </ListGroup.Item>
                      ))
                    ) : (
                      <ListGroup.Item>
                        {intl.formatMessage({
                          id: 'TELEPHONIE.LOCALISATIONS.FORM.ADDRESS_SEARCH.NO_RESULT',
                        })}
                      </ListGroup.Item>
                    )}
                  </ListGroup>
                )}
              </Col>
              <Col md={2}>
                <Form.Label className="fs-6">
                  {intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.FORM.LABEL_STREET_NUMBER'})}
                </Form.Label>
                <Form.Control
                  required
                  value={formulaire.numero}
                  onChange={(e) =>
                    setFormulaire(sansDonneesGeo({...formulaire, numero: e.target.value}))
                  }
                />
              </Col>
              <Col md={2}>
                <Form.Label className="fs-6">
                  {intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.FORM.LABEL_POSTAL_CODE'})}
                </Form.Label>
                <Form.Control
                  required
                  value={formulaire.cp}
                  onChange={(e) =>
                    setFormulaire(sansDonneesGeo({...formulaire, cp: e.target.value}))
                  }
                />
              </Col>
              <Col md={3}>
                <Form.Label className="fs-6">
                  {intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.FORM.LABEL_CITY'})}
                </Form.Label>
                <Form.Control
                  required
                  value={formulaire.ville}
                  onChange={(e) =>
                    setFormulaire(sansDonneesGeo({...formulaire, ville: e.target.value}))
                  }
                />
              </Col>
            </Row>
            <Row className="g-5 mb-5">
              <Col md={6}>
                <Form.Label className="fs-6">
                  {intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.FORM.LABEL_COUNTRY'})}
                </Form.Label>
                <Form.Select
                  required
                  value={formulaire.pays}
                  onChange={(e) =>
                    setFormulaire(sansDonneesGeo({...formulaire, pays: e.target.value}))
                  }
                >
                  <option>France</option>
                  <option>Suisse</option>
                  <option>Belgique</option>
                </Form.Select>
              </Col>
              {siteEnEdition && (
                <Col md={6}>
                  <Form.Label className="fs-6">
                    {intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.FORM.LABEL_DEFAULT_SITE'})}
                  </Form.Label>
                  <div className="d-flex gap-5">
                    <Form.Check
                      type="radio"
                      id="loc-def-oui"
                      name="loc-def"
                      label={intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.FORM.YES'})}
                      checked={formulaire.defaut}
                      onChange={() => setFormulaire({...formulaire, defaut: true})}
                    />
                    <Form.Check
                      type="radio"
                      id="loc-def-non"
                      name="loc-def"
                      label={intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.FORM.NO'})}
                      checked={!formulaire.defaut}
                      onChange={() => setFormulaire({...formulaire, defaut: false})}
                    />
                  </div>
                </Col>
              )}
            </Row>
            <div className="d-flex gap-3">
              <Button
                label={
                  siteEnEdition
                    ? intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.FORM.SUBMIT_EDIT'})
                    : intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.FORM.SUBMIT_ADD'})
                }
                disabled={
                  !formulaireValide ||
                  ajouterMutation.isLoading ||
                  modifierMutation.isLoading ||
                  resolutionBatiment.isLoading
                }
                onClick={validerFormulaire}
              />
              <BootstrapButton variant="secondary" size="sm" onClick={fermerFormulaire}>
                {intl.formatMessage({id: 'CANCEL'})}
              </BootstrapButton>
              {resolutionBatiment.isLoading && (
                <span className="d-flex align-items-center text-muted fs-7">
                  <Spinner animation="border" size="sm" className="me-2" />
                  {intl.formatMessage({
                    id: 'TELEPHONIE.LOCALISATIONS.FORM.ADDRESS_SEARCH.RESOLVING_BUILDING_REF',
                  })}
                </span>
              )}
            </div>
          </Card.Body>
        </Card>
      )}

      <Modal show={siteASupprimer != null} onHide={() => setSiteASupprimer(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill text-warning me-2" />
            {intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.DELETE.TITLE'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.DELETE.CONFIRM_TEXT'})}{' '}
          <strong>{siteASupprimer?.nom}</strong> ({siteASupprimer?.rue} {siteASupprimer?.numero},{' '}
          {siteASupprimer?.ville}) ?{' '}
          {intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.DELETE.IRREVERSIBLE'})}
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={() => setSiteASupprimer(null)}>
            {intl.formatMessage({id: 'CANCEL'})}
          </BootstrapButton>
          <BootstrapButton
            variant="danger"
            disabled={supprimerMutation.isLoading}
            onClick={confirmerSuppression}
          >
            {intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.SUPPRIMER'})}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>

      <Modal
        show={siteAAffecter != null}
        onHide={() => setSiteAAffecter(null)}
        backdrop={progressionAffectation ? 'static' : true}
        keyboard={!progressionAffectation}
        centered
      >
        <Modal.Header closeButton={!progressionAffectation}>
          <Modal.Title>
            {intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.ASSIGN.TITLE'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted fs-6 mb-4">
            {intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.ASSIGN.DESCRIPTION'})}{' '}
            <strong>{siteAAffecter?.nom}</strong> ({siteAAffecter?.rue} {siteAAffecter?.numero},{' '}
            {siteAAffecter?.ville}).
          </p>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span className="text-muted fs-7">
              {intl.formatMessage(
                {id: 'TELEPHONIE.LOCALISATIONS.ASSIGN.SELECTED_COUNT'},
                {count: numerosSelectionnes.length}
              )}
            </span>
            <span
              className="text-primary fs-7 cursor-pointer"
              onClick={() =>
                setNumerosSelectionnes((precedent) =>
                  precedent.length === numerosAssignables.length
                    ? []
                    : numerosAssignables.map((numero) => numero.numero)
                )
              }
            >
              {intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.ASSIGN.SELECT_ALL'})}
            </span>
          </div>
          <div className="border rounded" style={{maxHeight: 260, overflowY: 'auto'}}>
            {numerosAssignables.map((numero) => (
              <Form.Check
                key={numero.numero}
                type="checkbox"
                id={`affectation-${numero.numero}`}
                className="py-2 pe-5 border-bottom"
                style={{paddingLeft: '2.5rem'}}
                label={
                  <>
                    {numero.numero}
                    {numero.attribueA && (
                      <span className="text-muted fs-7"> — {numero.attribueA}</span>
                    )}
                  </>
                }
                checked={numerosSelectionnes.includes(numero.numero)}
                onChange={(e) =>
                  setNumerosSelectionnes(
                    e.target.checked
                      ? [...numerosSelectionnes, numero.numero]
                      : numerosSelectionnes.filter((n) => n !== numero.numero)
                  )
                }
              />
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          {progressionAffectation && (
            <span className="d-flex align-items-center text-muted fs-7 me-auto">
              <Spinner animation="border" size="sm" className="me-2" />
              {intl.formatMessage(
                {id: 'TELEPHONIE.LOCALISATIONS.ASSIGN.IN_PROGRESS'},
                {fait: progressionAffectation.fait, total: progressionAffectation.total}
              )}
            </span>
          )}
          <BootstrapButton
            variant="secondary"
            disabled={!!progressionAffectation}
            onClick={() => setSiteAAffecter(null)}
          >
            {intl.formatMessage({id: 'CANCEL'})}
          </BootstrapButton>
          <Button
            label={intl.formatMessage({id: 'TELEPHONIE.LOCALISATIONS.ASSIGN.CONFIRM'})}
            disabled={mettreAJourNumeroApi.isLoading || !!progressionAffectation}
            onClick={confirmerAffectation}
          />
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LocalisationsUrgence;
